import { useEffect } from "react";
import { useSignInWithEmailAndPassword, useSignOut } from "react-firebase-hooks/auth";
import { SubmitHandler, useForm } from "react-hook-form";
import { signInWithGoogle, getUser } from "../../apis/auth";
import { ROUTES } from "../../routes/Routes";
import { useParams, useNavigate } from "react-router-dom";
import { EMAIL_REGEX } from "../../constants";
import { ToastVariant, useToastContext } from "../../context";
import { auth } from "../../firebase";
import { ReactComponent as FrydaLogo } from "./../../assets/logo/Fryda_logo.svg";
import { ReactComponent as GoogleLogo } from "./../../assets/logo/google.svg";
import { useTranslation } from "react-i18next";


type LoginProps = {
  email: string;
  password: string;
};

const Login = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginProps>({ mode: "onBlur" });

  const [signInWithEmailAndPassword, , , error] =
    useSignInWithEmailAndPassword(auth);
  const [signOut] = useSignOut(auth);
  const { partnerId } = useParams();
  const navigate = useNavigate();

  const verifyPartnerAccess = async (uid: string) => {
    // If no partnerId in URL (legacy login), we might allow it or strictly enforce.
    // Assuming strict enforcement based on requirements.
    if (!partnerId) return;

    try {
      const userProfile: any = await getUser(uid);

      if (!userProfile) {
        console.error("User profile not found");
        await signOut();
        addToast(ToastVariant.ERROR, {
          title: "Error",
          description: "User profile not found.",
        });
        return;
      }

      // Check if user has the partnerId or is owner of it.
      // Adjust logic based on your exact user profile structure.
      // Assuming userProfile.partnerId is the field.
      if (userProfile.partnerId !== partnerId) {
        await signOut();
        addToast(ToastVariant.ERROR, {
          title: "Access Denied",
          description: "You do not have access to this partner environment.",
        });
        return;
      }
      // Success - redirect handled by App.tsx user state change or explicit navigate
      navigate('/dashboard');

    } catch (err) {
      console.error("Error verifying access", err);
      await signOut();
      addToast(ToastVariant.ERROR, {
        title: "Verification Error",
        description: "Could not verify your access permissions.",
      });
    }
  };


  const onSubmit: SubmitHandler<LoginProps> = async (data) => {
    try {
      const result = await signInWithEmailAndPassword(data.email, data.password);
      if (result?.user) {
        await verifyPartnerAccess(result.user.uid);
      }
    } catch (e) {
      console.error("login error", e);
    }
  };

  const { addToast } = useToastContext();

  useEffect(() => {
    if (error) {
      addToast(ToastVariant.ERROR, {
        title: t("login.error_title"),
        description: error.message,
      });
    }
  }, [error, addToast, t]);


  return (
    <div className="min-h-screen bg-gradient-to-r from-[#E1E3F9] from-10% via-[#CDD1FF] via-30% to-[#E6EFFD] to-90% flex items-center justify-center">
      <div className="flex items-center justify-center flex-col">
        <FrydaLogo />
        <div className="bg-white p-8 mt-6 rounded-lg shadow-2xl border-2 border-gray-600 w-96">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-5">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t("login.email")}
              </label>
              <input
                type="email"
                {...register("email", {
                  required: { value: true, message: t("login.email_required") },
                  pattern: { value: EMAIL_REGEX, message: t("login.invalid_email") },
                })}
                id="email"
                name="email"
                className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:outline-none"
              />
              <span className="text-red-500 text-xs">{errors?.email?.message}</span>
            </div>

            <div className="mb-5">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t("login.password")}
              </label>
              <input
                type="password"
                {...register("password", {
                  required: { value: true, message: t("login.password_required") },
                })}
                id="password"
                name="password"
                className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:outline-none"
              />
              <span className="text-red-500 text-xs">{errors?.password?.message}</span>
            </div>

            <button type="submit" className="w-full bg-black text-white p-3 rounded-md">
              {t("login.login_button")}
            </button>
          </form>

          <div className="flex items-center mt-6">
            <div className="w-1/2 border-b border-black-50"></div>
            <p className="mx-3">{t("login.or")}</p>
            <div className="w-1/2 border-b border-black-50"></div>
          </div>

          <div className="flex items-center justify-center mt-6">
            <button
              type="button"
              className="flex bg-white py-3 px-5 rounded-md border border-black"
              onClick={() => {
                signInWithGoogle()
                  .then(async (result) => {
                    if (result.user) {
                      await verifyPartnerAccess(result.user.uid);
                    }
                  })
                  .catch((error) => {
                    console.error(error);
                    window.location.href = ROUTES.LOGIN;
                  });
              }}
            >
              <GoogleLogo className="w-6 h-6 me-2" />
              {t("login.google_login")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
